import React, { type ComponentType, useCallback, useRef, useEffect, memo } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getNormalisedPerformanceFFs } from '@atlassian/jira-common-long-task-metrics/src/common/util/collectors.tsx';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ISSUE_HIERARCHY_LEVEL_EPIC } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useIccContext } from '@atlassian/jira-platform-inline-card-create/src/services/context/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import type {
	// eslint-disable-next-line jira/import/no-restricted-import
	RealtimeEventType,
	// eslint-disable-next-line jira/import/no-restricted-import
	RealtimeEvent,
} from '@atlassian/jira-realtime/src/common/types/events.tsx';
// eslint-disable-next-line jira/import/no-restricted-import
import type { IssueEventPayload } from '@atlassian/jira-realtime/src/common/types/payloads.tsx';
// eslint-disable-next-line jira/import/no-restricted-import
import { RealtimeClient } from '@atlassian/jira-realtime/src/services/realtime-client/index.tsx';
import { useIsAnyMenuOpen } from '@atlassian/jira-software-context-menu/src/controllers/context-menu/index.tsx';
import { useCloseMenuOnScroll } from '@atlassian/jira-software-context-menu/src/controllers/use-close-menu-on-scroll/index.tsx';
import { ScrollStateContextProvider } from '@atlassian/jira-software-fast-virtual/src/services/use-scroll-state/index.tsx';
import { LongTasksScrollObserver } from '@atlassian/jira-software-long-tasks-scroll-observer/src/ui/index.tsx';
import type { PartialVersion } from '@atlassian/jira-software-version-backlog-panel-relay/src/common/types.tsx';
import { View } from '@atlassian/jira-software-view-settings/src/common/types/constant.tsx';
import { ViewSetting } from '@atlassian/jira-software-view-settings/src/common/types/settings.tsx';
import { useViewSettingsActions } from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { ANALYTICS_SOURCE_BACKLOG } from '../../common/constants/analytics.tsx';
import type { IssueParent } from '../../model/issue-parent/types.tsx';
import { REALTIME_EVENTS } from '../../model/realtime/constants.tsx';
import type { CardId, PartialCardForVersionsPanel } from '../../model/types.tsx';
import { PAGE_LOAD_MARK_RENDER_END } from '../../services/utils/performance-analytics/index.tsx';
import { usePerformanceMetric } from '../../state/state-hooks/performance-metric.tsx';
import type { Props as CardListProps } from '../card-list/index.tsx';
import type { OwnProps as DetailsViewProps } from '../detail-view/index.tsx';
import Realtime from '../realtime/index.tsx';
import type { OwnProps as SubmitApdexProps } from '../submit-apdex/index.tsx';
import SyncFastVirtualSubscribers from '../sync-fast-virtual-subscribers/index.tsx';
import { DetailViewPanelWithErrorBoundary } from './detail-view-panel/index.tsx';
import { EpicPanelWithErrorBoundary } from './epic-panel/index.tsx';
import { MoreResults } from './more-results/index.tsx';
import { useAutoScroller } from './use-auto-scroller/index.tsx';
import { useDragAnalytics } from './use-drag-analytics/index.tsx';
import { useInitEpicData } from './use-init-epic-data/index.tsx';
import { VersionsPanelWithErrorBoundary } from './versions-panel/index.tsx';

const RenderEndMark = () => {
	const metric = usePerformanceMetric();
	useEffect(() => {
		metric.mark(PAGE_LOAD_MARK_RENDER_END);
	}, [metric]);
	return null;
};

export type Props = {
	isCacheHit: boolean;
	isDetailViewVisible: boolean;
	isCompanyManaged?: boolean;
	issueHierarchyLevel: number;
	boardId: number;
	parentWidth: number;
	cardListIds: string[];
	DetailView: ComponentType<DetailsViewProps>;
	SubmitApdex: ComponentType<SubmitApdexProps>;
	CardList: ComponentType<CardListProps>;
	selectedCards: PartialCardForVersionsPanel[];
	BoardIssueMoveDialog: ComponentType<{}>;
	selectedCardTypeId: number | undefined;
	epicLinkFieldId?: string;
	firstNonEmptyCardListId?: string;
	lastNonEmptyCardListId?: string;
	onEpicPanelClose: () => void;
	onVersionPanelClose: () => void;
	onCardDetailClick: (cardId: CardId) => void;
	onEpicCreate: (issueParents: IssueParent[]) => void;
	onEpicAssign: (
		cards: { id: number }[],
		parentId: number | null,
		requestPromise: Promise<undefined>,
	) => void;
	onVersionAltered: (version: PartialVersion) => void;
	onIssueVersionsUpdated: (cardIds: CardId[]) => void;
	onReceive: (
		eventType: RealtimeEventType,
		payload: IssueEventPayload,
		analyticsEvent: UIAnalyticsEvent,
	) => void; // TODO: remove with 'jira_replace_provider_and_client_with_realtime' feature gate clean up
	onReceiveEvent: (event: RealtimeEvent) => void;
};

const BacklogContentInner = ({
	isCacheHit,
	boardId,
	issueHierarchyLevel,
	isDetailViewVisible,
	isCompanyManaged = false,
	parentWidth,
	CardList,
	cardListIds,
	selectedCards,
	SubmitApdex,
	BoardIssueMoveDialog,
	selectedCardTypeId,
	epicLinkFieldId,
	firstNonEmptyCardListId,
	lastNonEmptyCardListId,
	onEpicAssign,
	onCardDetailClick,
	onEpicCreate,
	onIssueVersionsUpdated,
	onVersionAltered,
	onReceive,
	onReceiveEvent,
}: Props) => {
	const scrollElementRef = useRef<HTMLDivElement | null>(null);
	const { onChangeCardType } = useIccContext();

	// NOTE: Update the selected issue type in ICC when the issue type filter is changed. Doing it here to avoid unnecessary update
	useEffect(() => {
		if (selectedCardTypeId !== undefined) {
			onChangeCardType(selectedCardTypeId);
		}
	}, [onChangeCardType, selectedCardTypeId]);

	const previousCardListIds = usePrevious(cardListIds);

	const isNewCardListAdded = (id: string) => {
		if (!previousCardListIds || previousCardListIds.length === 0) {
			return false;
		}

		return !previousCardListIds.includes(id);
	};

	useAutoScroller();
	useDragAnalytics(issueHierarchyLevel);
	useCloseMenuOnScroll(scrollElementRef);
	useInitEpicData(boardId);

	const { toggleViewSetting } = useViewSettingsActions();

	const isContextMenuOpened = useIsAnyMenuOpen();

	const handleVersionPanelClose = useCallback(() => {
		toggleViewSetting({ boardId, setting: ViewSetting.VERSION_PANEL, view: View.BACKLOG });
	}, [boardId, toggleViewSetting]);

	const handleEpicPanelClose = useCallback(() => {
		toggleViewSetting({ boardId, setting: ViewSetting.EPIC_PANEL, view: View.BACKLOG });
	}, [boardId, toggleViewSetting]);

	const content = (
		<UFOSegment name="backlog-container">
			<VersionsPanelWithErrorBoundary
				boardId={boardId}
				selectedIssues={selectedCards}
				onClose={handleVersionPanelClose}
				onVersionAltered={onVersionAltered}
				onVersionAssigned={onIssueVersionsUpdated}
				onVersionsUnassigned={onIssueVersionsUpdated}
				isCompanyManaged={isCompanyManaged}
			/>
			<EpicPanelWithErrorBoundary
				boardId={boardId}
				selectedIssues={selectedCards}
				issueHierarchyLevel={ISSUE_HIERARCHY_LEVEL_EPIC}
				epicLinkFieldId={epicLinkFieldId}
				onEpicPanelClose={handleEpicPanelClose}
				onCardDetailClick={onCardDetailClick}
				onEpicCreate={onEpicCreate}
				onEpicAssign={onEpicAssign}
				isCompanyManaged={isCompanyManaged}
			/>
			<Scrollable
				// eslint-disable-next-line jira/integration/enforce-data-testid-usage
				data-test-id="software-backlog.backlog-content.scrollable"
				data-testid="software-backlog.backlog-content.scrollable"
				ref={scrollElementRef}
				isContextMenuOpened={isContextMenuOpened}
			>
				<BoardIssueMoveDialog />
				<UFOSegment name="backlog-card-list">
					<ScrollStateContextProvider
						scrollRef={scrollElementRef}
						shouldSkipFirstResize={fg('jira-concurrent-backlog-perf')}
						initialScrollState={
							fg('jira-concurrent-backlog-perf')
								? {
										height: globalThis.innerHeight,
										width: 0,
										scrollHeight: globalThis.innerHeight,
										scrollWidth: 0,
										scrollTop: 0,
										scrollLeft: 0,
										isScrolling: false,
										scrollElement: null,
									}
								: undefined
						}
					>
						{cardListIds.map((id: string, index: number) => (
							<CardList
								key={id}
								id={id}
								isFirstList={index === 0}
								isFirstNonEmptyList={firstNonEmptyCardListId === id}
								isLastNonEmptyList={lastNonEmptyCardListId === id}
								justCreated={isNewCardListAdded(id)}
							/>
						))}
						<MoreResults scrollableParentRef={scrollElementRef} />
					</ScrollStateContextProvider>
				</UFOSegment>
				<SyncFastVirtualSubscribers />
				{fg('jsw_refactor_long-tasks_monitoring') ? (
					<LongTasksScrollObserver
						appName={ANALYTICS_SOURCE_BACKLOG}
						scrollElRef={scrollElementRef}
						attributes={getNormalisedPerformanceFFs()}
					/>
				) : (
					<LongTasksScrollObserver
						appName={ANALYTICS_SOURCE_BACKLOG}
						scrollEl={scrollElementRef.current}
					/>
				)}
			</Scrollable>
			<DetailViewPanelWithErrorBoundary
				boardId={boardId}
				parentWidth={parentWidth}
				isDetailViewVisible={isDetailViewVisible}
			/>
			{cardListIds.length > 0 && (
				<>
					<RenderEndMark />
					<SubmitApdex isCacheHit={isCacheHit} />
				</>
			)}
			{fg('jira_replace_provider_and_client_with_realtime') ? (
				<Realtime onReceive={onReceiveEvent} />
			) : (
				<Realtime>
					<RealtimeClient events={REALTIME_EVENTS} onReceive={onReceive} />
				</Realtime>
			)}
		</UFOSegment>
	);

	return isVisualRefreshEnabled() ? (
		<Box data-onboarding-observer-id="backlog-wrapper" xcss={wrapperStyles}>
			{content}
		</Box>
	) : (
		<PageWrapper data-onboarding-observer-id="backlog-wrapper">{content}</PageWrapper>
	);
};

const BacklogContent = memo(BacklogContentInner);
export default BacklogContent;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageWrapper = styled.div({
	paddingLeft: `${token('space.500')}`,
	display: 'flex',
	flexDirection: 'row',
	height: '100%',
	maxHeight: '100%',
	overflow: 'hidden',
	flex: 1,
});

const wrapperStyles = xcss({
	paddingLeft: 'space.300',
	display: 'flex',
	flexDirection: 'row',
	height: '100%',
	maxHeight: '100%',
	overflow: 'hidden',
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Scrollable = styled.div<{ isContextMenuOpened: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	padding: () =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		`0 ${isVisualRefreshEnabled() ? token('space.300') : token('space.400')} ${token('space.600')} 0`,
	flex: 1,
	flexDirection: 'column',
	maxHeight: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values
	overflowY: () => (getWillShowNav4() ? 'auto' : 'scroll'),
	overflowX: 'auto',
	userSelect: 'none',
	position: 'relative',
	willChange: 'opacity',
	// z-index ensures card context menu is positioned on
	// top of right-panel content, e.g. issue view
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	zIndex: ({ isContextMenuOpened }) => (isContextMenuOpened ? layers.dialog : layers.card),
});
